import { useMemo } from 'react';

import { useFormat, useLocale, useSelectedCompany } from '../../../../hooks/index';
import { IssuedSharesFormula } from '../../../../types/company.types';
import { CompanyIncorporationEvent } from '../../../../types/events.types';
import {
  Snapshot,
  SnapshotListItem,
  SnapshotSummaryStakeholder,
} from '../../../../types/snapshot.types';
import { toNumber } from '../../../../utils/to-number.util';
import { GraphVariant } from '../../variables';
import { aggregateIncorporationStakeholders } from './aggregate-incorporation-stakeholders.util';

export type EventGraphProps = {
  companyIncorporationEvent?: CompanyIncorporationEvent | null;
  currentSnapshot?: Snapshot;
  // Slim `/cap-table-snapshots` rows — the graph only needs per-stakeholder {id, name, diluted}
  // per point.
  snapshots: SnapshotListItem[];
  variant: GraphVariant;
};

type GraphStakeholder = {
  consideredPay?: number;
  id: string;
  name: string;
  percentage: number;
  shares: number;
  value: number;
};

export interface GraphData {
  chartLabel: string;
  date: string;
  dateIndex?: number;
  isCompanyIncorporation?: boolean;
  name: string;
  parValue?: string;
  poolAvailableShares?: number;
  poolValue?: number;
  postMoneyValuation: number;
  preMoneyValuation: number;
  stakeholders: GraphStakeholder[];
  totalConsideredPay?: number;
  totalIssuedShares?: number;
}

interface UseEventGraphDataReturn {
  allStakeholders: string[];
  normalizedData: GraphData[];
  sharesDivider: number;
  sharesRound: number;
  sharesSuffix: string;
  valuationUsesShareCount: boolean;
  valueDivider: number;
  valueRound: number;
  valueSuffix: string;
  yDomain: number[];
}

export const valuationChartUsesShareCount = (snapshots: SnapshotListItem[]): boolean => {
  const eventSnapshots = dedupeSnapshotsByValuationId(snapshots.filter(isEventValuationSnapshot));
  if (eventSnapshots.length === 0) return false;

  return eventSnapshots.every((snapshot) => toNumber(snapshot.valuation?.sharePrice) === 0);
};

export const poolUnissuedPoint = {
  name: 'Pool (Unissued)',
  id: 'poolUnissued',
};

export const othersPoint = {
  name: 'Others',
  id: 'others',
};

export const notExistingName = 'notExisting';

type SnapshotWithValuation = {
  valuation: NonNullable<SnapshotListItem['valuation']>;
} & SnapshotListItem;

// The cap-table list keeps incomplete legacy rows (the cap table page lists every row). Their
// trimmed stakeholders summary is an empty array, so only `hasCompleteStats` keeps one from
// plotting as a zero point.
const isEventValuationSnapshot = (snapshot: SnapshotListItem): snapshot is SnapshotWithValuation =>
  Boolean(snapshot.valuation) &&
  !snapshot.createdByCron &&
  snapshot.hasCompleteStats !== false &&
  Array.isArray(snapshot.stats?.stakeholders);

// Every point plots fully diluted cap-table shares — the basis of the Fully Diluted point and of
// the post-money valuation — so the stack only moves when the cap table does. A list body from a
// backend that predates `diluted` falls back to issued shares.
const pointShares = (stakeholder: SnapshotSummaryStakeholder): number =>
  stakeholder.diluted ?? stakeholder.issued;

const dedupeSnapshotsByValuationId = (
  snapshots: SnapshotWithValuation[],
): SnapshotWithValuation[] => {
  const latestByValuationId = new Map<string, SnapshotWithValuation>();

  for (const snapshot of snapshots) {
    const valuationId = snapshot.valuation.id;
    const existing = latestByValuationId.get(valuationId);
    if (!existing || new Date(snapshot.createdAt) > new Date(existing.createdAt)) {
      latestByValuationId.set(valuationId, snapshot);
    }
  }

  return Array.from(latestByValuationId.values());
};

export const useEventGraphData = ({
  snapshots,
  currentSnapshot,
  variant,
  companyIncorporationEvent,
}: EventGraphProps): UseEventGraphDataReturn => {
  const { format } = useFormat();
  const { messagesLocale } = useLocale();
  const { selectedCompany } = useSelectedCompany();

  const issuedSharesFormula =
    selectedCompany?.issuedSharesFormula || IssuedSharesFormula.ISSUED_SHARES_AND_AVAILABLE_POOL;

  const getDividerInfo = (values: number[]) => {
    const getDivider = (max: number) => {
      if (max < 1000) return { divider: 1, round: 1, suffix: '' };
      if (max < 1000000) return { divider: 1000, round: 1, suffix: 'k' };
      if (max < 1000000000) return { divider: 1000000, round: max < 10000000 ? 1 : 0, suffix: 'M' };
      return { divider: 1000000000, round: 1, suffix: 'B' };
    };

    const max = values.length > 0 ? Math.max(...values) : 0;
    return getDivider(max);
  };

  const sortedByDateSnapshots = useMemo(() => {
    return dedupeSnapshotsByValuationId(snapshots.filter(isEventValuationSnapshot)).sort(
      (a, b) => new Date(a.valuation.date).getTime() - new Date(b.valuation.date).getTime(),
    );
  }, [snapshots]);

  const valuationUsesShareCount = useMemo(
    () => valuationChartUsesShareCount(snapshots),
    [snapshots],
  );

  const rawData = useMemo(() => {
    return sortedByDateSnapshots.map(
      ({ stats, valuation, postMoneyValuation, poolDilutedShares, poolGrantedShares }) => {
        const stakeholdersShares = toNumber(
          stats.stakeholders?.reduce((total, stakeholder) => {
            return total + pointShares(stakeholder);
          }, 0),
        );

        const totalShares =
          issuedSharesFormula === IssuedSharesFormula.ISSUED_SHARES_AND_AVAILABLE_POOL
            ? stakeholdersShares + poolDilutedShares - poolGrantedShares
            : stakeholdersShares;

        const formattedDate = format(valuation.date, 'dd/MM/yyyy');

        return {
          date: formattedDate,
          chartLabel: `${formattedDate}|${valuation.name}`,
          postMoneyValuation,
          preMoneyValuation: (valuation.issuedSharesOnStart || 0) * (valuation.sharePrice || 0),
          name: valuation.name,
          poolAvailableShares: poolDilutedShares - poolGrantedShares,
          poolValue: (poolDilutedShares - poolGrantedShares) * (valuation.sharePrice || 0),
          stakeholders: stats.stakeholders.map((stakeholder) => {
            const shares = pointShares(stakeholder);
            return {
              id: stakeholder.id,
              name: stakeholder.name,
              shares,
              percentage: totalShares > 0 ? ((shares || 0) * 100) / totalShares || 0 : 0,
              value: shares * (valuation.sharePrice || 0),
            };
          }),
        };
      },
    );
  }, [sortedByDateSnapshots, issuedSharesFormula, format]);

  const rawSharesValues = rawData.map((item) => {
    const stakeholdersSharesValue = item.stakeholders.reduce(
      (sum, stakeholder) => sum + (stakeholder.value || 0),
      0,
    );
    return issuedSharesFormula === IssuedSharesFormula.ISSUED_SHARES_AND_AVAILABLE_POOL
      ? stakeholdersSharesValue + item?.poolValue
      : stakeholdersSharesValue;
  });

  const rawTotalSharesPerValuation = rawData.map((item) => {
    const stakeholdersSharesValue = item.stakeholders.reduce(
      (sum, stakeholder) => sum + (stakeholder.shares || 0),
      0,
    );
    return issuedSharesFormula === IssuedSharesFormula.ISSUED_SHARES_AND_AVAILABLE_POOL
      ? stakeholdersSharesValue + item?.poolAvailableShares
      : stakeholdersSharesValue;
  });

  const {
    divider: valueDivider,
    suffix: valueSuffix,
    round: valueRound,
  } = getDividerInfo(rawSharesValues);
  const {
    divider: sharesDivider,
    suffix: sharesSuffix,
    round: sharesRound,
  } = getDividerInfo(rawTotalSharesPerValuation);

  const data = rawData.map(
    ({
      chartLabel,
      date,
      name,
      postMoneyValuation,
      stakeholders,
      poolAvailableShares,
      poolValue,
      preMoneyValuation,
    }) => {
      return {
        chartLabel,
        date,
        postMoneyValuation,
        preMoneyValuation,
        name,
        poolAvailableShares: poolAvailableShares / sharesDivider,
        poolValue: poolValue / valueDivider,
        stakeholders: stakeholders.map(({ id, name, percentage, shares, value }) => ({
          id,
          name,
          shares: toNumber(shares / sharesDivider),
          percentage,
          value: toNumber(value / valueDivider),
        })),
      };
    },
  );

  const allStakeholders = useMemo(() => {
    return Array.from(
      new Set(
        data.flatMap((snapshot) => snapshot.stakeholders.map((stakeholder) => stakeholder.id)),
      ),
    );
  }, [data]);

  const fullyDilutedPoint = useMemo(() => {
    if (!currentSnapshot)
      return {
        chartLabel: messagesLocale['FULLY_DILUTED'],
        date: messagesLocale['FULLY_DILUTED'],
        postMoneyValuation: 0,
        preMoneyValuation: 0,
        name: messagesLocale['FULLY_DILUTED'],
        poolAvailableShares: 0,
        sharePrice: 0,
        stakeholders: allStakeholders.map((id) => ({
          id,
          name: notExistingName,
          shares: 0,
          percentage: 0,
          value: 0,
        })),
      };

    const {
      dilutedShares,
      poolDilutedShares,
      poolGrantedShares,
      valuation,
      stats,
      postMoneyValuation,
    } = currentSnapshot;

    const stakeholders = stats?.stakeholders ?? [];
    const poolAvailableShares = poolDilutedShares - poolGrantedShares;
    const price = valuation?.sharePrice ?? 0;

    return {
      date: messagesLocale['FULLY_DILUTED'],
      chartLabel: messagesLocale['FULLY_DILUTED'],
      postMoneyValuation: dilutedShares * price,
      preMoneyValuation: postMoneyValuation,
      name: messagesLocale['FULLY_DILUTED'],
      poolAvailableShares: poolAvailableShares / sharesDivider,
      poolValue: (poolAvailableShares * price) / valueDivider,
      stakeholders: stakeholders.map(({ id, name, diluted }) => ({
        id,
        name,
        shares: toNumber(diluted / sharesDivider),
        percentage: ((diluted || 0) * 100) / dilutedShares,
        value: toNumber((diluted * price) / valueDivider),
      })),
    };
  }, [allStakeholders, currentSnapshot, messagesLocale, sharesDivider, valueDivider]);

  const incorporationPoint = useMemo(() => {
    const items = companyIncorporationEvent?.items;
    if (!companyIncorporationEvent || !Array.isArray(items)) return null;

    const parValue = toNumber(companyIncorporationEvent.parValue) ?? 0;
    const { stakeholders, totalConsideredPay, totalIssuedShares } =
      aggregateIncorporationStakeholders({ items, parValue, sharesDivider });
    const formattedDate = format(companyIncorporationEvent.date, 'dd/MM/yyyy');
    const incorporationName = messagesLocale['INCORPORATION'] ?? 'Incorporation';

    return {
      chartLabel: `${formattedDate}|${incorporationName}`,
      date: formattedDate,
      isCompanyIncorporation: true,
      name: incorporationName,
      parValue: companyIncorporationEvent.parValue,
      postMoneyValuation: 0,
      preMoneyValuation: 0,
      totalConsideredPay,
      totalIssuedShares,
      poolAvailableShares: 0,
      poolValue: 0,
      stakeholders,
    };
  }, [companyIncorporationEvent, format, messagesLocale, sharesDivider]);

  const normalizedData = useMemo(() => {
    const lastPoint = fullyDilutedPoint;

    const includePool =
      lastPoint?.poolAvailableShares > 0 &&
      issuedSharesFormula === IssuedSharesFormula.ISSUED_SHARES_AND_AVAILABLE_POOL;

    const lastPointStakeholdersShares = lastPoint.stakeholders.reduce((total, stakeholder) => {
      return total + stakeholder.shares;
    }, 0);

    const totalShares =
      issuedSharesFormula === IssuedSharesFormula.ISSUED_SHARES_AND_AVAILABLE_POOL
        ? lastPointStakeholdersShares + lastPoint?.poolAvailableShares
        : lastPointStakeholdersShares;

    const lastSnapshotStakeholders: GraphStakeholder[] = lastPoint
      ? lastPoint.stakeholders.sort((a, b) => b.shares - a.shares)
      : [];

    const incorporationStakeholderIdsWithShares = (incorporationPoint?.stakeholders ?? [])
      .filter((stakeholder) => stakeholder.shares > 0)
      .map((stakeholder) => stakeholder.id);

    const includeOthers = lastSnapshotStakeholders.length >= 10;
    const lastSnapshotTopStakeholders = lastSnapshotStakeholders.slice(0, 10);
    const lastSnapshotTopStakeholdersIds = lastSnapshotTopStakeholders.map((s) => s.id);
    const others = lastSnapshotStakeholders.filter(
      (s) => !lastSnapshotTopStakeholdersIds.includes(s.id) && s.id !== poolUnissuedPoint.id,
    );

    if (includeOthers && others.length > 0) {
      const othersAggregated = others.reduce(
        (acc, stakeholder) => {
          acc.shares += stakeholder.shares;
          acc.percentage += stakeholder.percentage;
          acc.value += stakeholder.value;
          return acc;
        },
        { id: othersPoint.id, name: othersPoint.name, shares: 0, percentage: 0, value: 0 },
      );
      lastSnapshotTopStakeholders.push(othersAggregated);
    }

    if (includePool) {
      const poolStakeholder =
        lastPoint.poolAvailableShares > 0
          ? {
              id: poolUnissuedPoint.id,
              name: poolUnissuedPoint.name,
              shares: lastPoint.poolAvailableShares || 0,
              value: lastPoint.poolValue || 0,
              percentage: (lastPoint.poolAvailableShares * 100) / totalShares,
            }
          : {
              id: poolUnissuedPoint.id,
              name: poolUnissuedPoint.name,
              shares: 0,
              value: 0,
              percentage: 0,
            };

      lastSnapshotTopStakeholders.push(poolStakeholder);
    }

    for (const stakeholder of incorporationPoint?.stakeholders ?? []) {
      if (
        stakeholder.shares <= 0 ||
        lastSnapshotTopStakeholders.some((existing) => existing.id === stakeholder.id)
      ) {
        continue;
      }

      // Keep incorporation-only stakeholders visible as dedicated series.
      lastSnapshotTopStakeholders.push({
        id: stakeholder.id,
        name: stakeholder.name,
        shares: 0,
        percentage: 0,
        value: 0,
        consideredPay: 0,
      });
    }

    const alignmentTopStakeholderIds = Array.from(
      new Set([...incorporationStakeholderIdsWithShares, ...lastSnapshotTopStakeholdersIds]),
    );

    const lastPointFinalTopStakeholders = lastSnapshotTopStakeholders.sort(
      (a, b) => b.shares - a.shares,
    );

    const alignStakeholdersToLastPoint = ({
      poolAvailableShares = 0,
      poolValue = 0,
      sourceStakeholders,
    }: {
      poolAvailableShares?: number;
      poolValue?: number;
      sourceStakeholders: GraphStakeholder[];
    }): GraphStakeholder[] => {
      const stakeholderIdsForAlignment = Array.from(
        new Set([
          ...alignmentTopStakeholderIds,
          ...allStakeholders,
          ...sourceStakeholders.map((stakeholder) => stakeholder.id),
        ]),
      );

      const stakeholders = stakeholderIdsForAlignment.map((id) => {
        const existing = sourceStakeholders.find((stakeholder) => stakeholder.id === id);
        return existing
          ? existing
          : {
              id,
              name: notExistingName,
              shares: 0,
              percentage: 0,
              value: 0,
            };
      });

      const stakeholdersShares = sourceStakeholders.reduce((total, stakeholder) => {
        return total + stakeholder.shares;
      }, 0);

      const pointTotalShares =
        issuedSharesFormula === IssuedSharesFormula.ISSUED_SHARES_AND_AVAILABLE_POOL
          ? stakeholdersShares + poolAvailableShares
          : stakeholdersShares;

      const topStakeholders = stakeholders.filter((s) => alignmentTopStakeholderIds.includes(s.id));

      const othersStakeholders = stakeholders.filter(
        (s) => !alignmentTopStakeholderIds.includes(s.id) && s.id !== poolUnissuedPoint.id,
      );

      if (includeOthers && othersStakeholders.length > 0) {
        const othersAggregated = othersStakeholders.reduce(
          (acc, stakeholder) => {
            acc.shares += stakeholder.shares;
            acc.percentage += stakeholder.percentage;
            acc.value += stakeholder.value;
            acc.consideredPay = (acc.consideredPay || 0) + (stakeholder.consideredPay || 0);
            return acc;
          },
          {
            id: othersPoint.id,
            name: othersPoint.name,
            shares: 0,
            percentage: 0,
            value: 0,
            consideredPay: 0,
          },
        );
        topStakeholders.push(othersAggregated);
      }

      if (includePool) {
        topStakeholders.push(
          poolAvailableShares > 0
            ? {
                id: poolUnissuedPoint.id,
                name: poolUnissuedPoint.name,
                shares: poolAvailableShares || 0,
                value: poolValue || 0,
                percentage:
                  pointTotalShares > 0 ? (poolAvailableShares * 100) / pointTotalShares : 0,
              }
            : {
                id: poolUnissuedPoint.id,
                name: poolUnissuedPoint.name,
                shares: 0,
                value: 0,
                percentage: 0,
              },
        );
      }

      return topStakeholders.sort((a, b) => {
        const lastSnapshotIndexA = lastPointFinalTopStakeholders.findIndex(
          (stakeholder) => stakeholder.id === a.id,
        );
        const lastSnapshotIndexB = lastPointFinalTopStakeholders.findIndex(
          (stakeholder) => stakeholder.id === b.id,
        );
        return lastSnapshotIndexA - lastSnapshotIndexB;
      });
    };

    const snapshotsData = data.map((snapshot, index) => ({
      ...snapshot,
      stakeholders: alignStakeholdersToLastPoint({
        sourceStakeholders: snapshot.stakeholders,
        poolAvailableShares: snapshot.poolAvailableShares,
        poolValue: snapshot.poolValue,
      }),
      dateIndex: index,
    }));

    const incorporationOffset = incorporationPoint ? 1 : 0;

    return [
      ...(incorporationPoint
        ? [
            {
              ...incorporationPoint,
              stakeholders: alignStakeholdersToLastPoint({
                sourceStakeholders: incorporationPoint.stakeholders,
                poolAvailableShares: 0,
                poolValue: 0,
              }),
              dateIndex: 0,
            },
          ]
        : []),
      ...snapshotsData.map((snapshot, index) => ({
        ...snapshot,
        dateIndex: index + incorporationOffset,
      })),
      {
        ...lastPoint,
        stakeholders: lastPointFinalTopStakeholders,
        dateIndex: snapshotsData.length + incorporationOffset,
      },
    ];
  }, [allStakeholders, data, fullyDilutedPoint, incorporationPoint, issuedSharesFormula]);

  const sharesValues = normalizedData.map((item) => {
    const totalValues = item.stakeholders.reduce((sum, stakeholder) => {
      return sum + (stakeholder.value || 0);
    }, 0);
    return totalValues;
  });
  const maxValuation = sharesValues.length > 0 ? Math.max(...sharesValues) : 0;

  const totalSharesPerValuation = normalizedData.map((item) => {
    const totalShares = item.stakeholders.reduce((sum, stakeholder) => {
      return sum + (stakeholder.shares || 0);
    }, 0);
    return totalShares;
  });
  const maxShares = totalSharesPerValuation.length > 0 ? Math.max(...totalSharesPerValuation) : 0;

  const valuationChartStacksShares = variant === GraphVariant.VALUATION && valuationUsesShareCount;

  const maxYValue =
    variant === GraphVariant.OWNERSHIP || valuationChartStacksShares ? maxShares : maxValuation;
  const yDomain = variant === GraphVariant.OWNERSHIP_PERCENT ? [0, 100] : [0, maxYValue];

  const finalStakeholders = useMemo(() => {
    const lastSnapshot = normalizedData[normalizedData.length - 1];
    return lastSnapshot?.stakeholders.map((s) => s.id) ?? [];
  }, [normalizedData]);

  return {
    normalizedData,
    sharesRound,
    sharesDivider,
    sharesSuffix,
    valuationUsesShareCount,
    valueRound,
    valueDivider,
    valueSuffix,
    allStakeholders: finalStakeholders,
    yDomain,
  };
};
